// src/components/settings/UserSettings.tsx
import React, { useState } from "react";
import SettingsSidebar from "./SettingsSidebar";
import "./Settings.scss";

type User = {
  id: number;
  name: string;
  email: string;
  role: "user" | "admin";
  groups: string[];
};

const availableGroups = ["Marketing", "Sales", "Entwicklung", "Support"];

const UserSettings: React.FC = () => {
  const [users, setUsers] = useState<User[]>([
    { id: 1, name: "Max Mustermann", email: "max@beispiel.com", role: "user", groups: ["Marketing"] },
    { id: 2, name: "Erika Müller",    email: "erika@beispiel.com", role: "admin", groups: ["Entwicklung", "Support"] },
    { id: 3, name: "John Doe",        email: "john@beispiel.com", role: "user", groups: [] },
  ]);

  const [newUser, setNewUser] = useState<Omit<User, "id">>({
    name: "",
    email: "",
    role: "user",
    groups: [],
  });

  const toggleRole = (id: number) => {
    setUsers(users.map(u =>
      u.id === id ? { ...u, role: u.role === "user" ? "admin" : "user" } : u
    ));
  };

  const toggleGroup = (userId: number, group: string) => {
    setUsers(users.map(u => {
      if (u.id !== userId) return u;
      const has = u.groups.includes(group);
      const groups = has
        ? u.groups.filter(g => g !== group)
        : [...u.groups, group];
      return { ...u, groups };
    }));
  };

  const handleNewGroup = (group: string) => {
    const has = newUser.groups.includes(group);
    setNewUser({
      ...newUser,
      groups: has
        ? newUser.groups.filter(g => g !== group)
        : [...newUser.groups, group]
    });
  };

  const handleAddUser = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newUser.name || !newUser.email) return;
    const nextId = users.length ? Math.max(...users.map(u => u.id)) + 1 : 1;
    setUsers([...users, { id: nextId, ...newUser }]);
    setNewUser({ name: "", email: "", role: "user", groups: [] });
  };

  return (
    <div className="settings-container">
      <SettingsSidebar />

      <div className="settings-content">
        <div className="user-management-section">
          <h2>Benutzerverwaltung</h2>

          {/* Neue Nutzer hinzufügen */}
          <form className="add-user-form modern-card" onSubmit={handleAddUser}>
            <h3>Neuen Nutzer hinzufügen</h3>
            <div className="form-grid">
              <div className="input-group">
                <label>Name</label>
                <input
                  type="text"
                  value={newUser.name}
                  onChange={e => setNewUser({ ...newUser, name: e.target.value })}
                  required
                />
              </div>
              <div className="input-group">
                <label>Email</label>
                <input
                  type="email"
                  value={newUser.email}
                  onChange={e => setNewUser({ ...newUser, email: e.target.value })}
                  required
                />
              </div>
              <div className="input-group">
                <label>Rolle</label>
                <select
                  value={newUser.role}
                  onChange={e => setNewUser({ ...newUser, role: e.target.value as "user"|"admin" })}
                >
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <div className="input-group groups-input">
                <label>Gruppen</label>
                <div className="groups">
                  {availableGroups.map(g => (
                    <label key={g} className="group-checkbox">
                      <input
                        type="checkbox"
                        checked={newUser.groups.includes(g)}
                        onChange={() => handleNewGroup(g)}
                      />
                      {g}
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <div className="form-actions">
              <button type="submit" className="save-btn">Nutzer hinzufügen</button>
            </div>
          </form>

          {/* Bestehende Nutzer */}
          <div className="user-grid modern-card">
            {users.map(user => (
              <div key={user.id} className="user-card">
                <div className="user-info">
                  <div className="name">{user.name}</div>
                  <div className="email">{user.email}</div>
                </div>

                <div className="role-toggle">
                  <label>
                    Rolle:
                    <button
                      className={user.role === "admin" ? "admin-btn" : "user-btn"}
                      onClick={() => toggleRole(user.id)}
                    >
                      {user.role === "admin" ? "Admin" : "User"}
                    </button>
                  </label>
                </div>

                <div className="group-select">
                  <label>Gruppen zuweisen:</label>
                  <div className="groups">
                    {availableGroups.map(group => (
                      <label key={group} className="group-checkbox">
                        <input
                          type="checkbox"
                          checked={user.groups.includes(group)}
                          onChange={() => toggleGroup(user.id, group)}
                        />
                        {group}
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserSettings;
